import { log, assert, error } from '../app/logger.js';
import { locations } from '../data/locations.js';
import { LocationFilter } from '../data/location_filter.js';
import { GpxTrack } from './gpx_track.js';
import { Coords } from './coords.js';

/**
 * Locations on the map.
 */
class MapLocations {
  on_marker_click = null;
  on_marker_dblclick = null;

  constructor(map) {
    this.#map = map;
  }

  loadInView() {
    let { north, east, south, west } = this.#map.bounds;
    if (!north || !east || !south || !west) {
      error(
        `Failed to retrieve maps bounds: north: ${north}, east: ${east}, south: ${south}, west: ${west}`
      );
      return null;
    }
    return locations.getInArea({ north, east, south, west }).then(locs => {
      for (let loc of locs) {
        this.#setLocOnMap(loc);
      }
      return this.filter();
    });
  }

  load(id) {
    return locations
      .get(id)
      .loadIfNeeded()
      .then(loc => this.#setLocOnMap(loc));
  }

  from(locData) {
    return locations.from(locData);
  }

  byGoogleId(google_id) {
    return locations.byGoogleId(google_id);
  }

  asList() {
    return locations.asList();
  }

  namesOfVisible() {
    return Array.from(locations.asList())
      .filter(l => l.marker && l.marker.visible)
      .map(l => ({
        name: l.name,
      }));
  }

  async filter(filter) {
    let { gap_lat, gap_lng, south, east, north, west } =
      await Coords.distanceInPixel(this.#map);
    if (!south || !east || !north || !west) {
      error(`[map:locfilter-on-map] no bounds to filter locations`);
      return;
    }

    gap_lat *= 50;
    gap_lng *= 50;

    log(
      `[map:locfilter-on-map] filter locations: {
  gap_lat: ${gap_lat},
  gap_lng: ${gap_lng},
  south: ${south},
  north: ${north},
  east: ${east},
  west: ${west}
}`
    );

    const mapZoom = this.#map.zoom;
    if (filter) {
      this.#activeFilter = filter;
    }

    let locs = Array.from(locations.asList()).filter(loc => loc.marker);
    LocationFilter.filter({
      locs,
      gap_lat,
      gap_lng,
      south,
      north,
      east,
      west,
      filter: this.#activeFilter,
      mapZoom,
    });

    for (let loc of locs) {
      if (loc.visible !== undefined) {
        loc.marker.visible = loc.visible;
      }
    }
  }

  // Adds a new location.
  async addLoc(data) {
    let loc = await locations.add(data);
    return loc && this.#setLocOnMap(loc);
  }

  // Changes existing location info.
  changeLoc(data) {
    let changePromise = locations.change(data);

    // Update marker if any.
    let loc = locations.get(data.id);
    if (loc.marker) {
      if (this.#shouldHaveMarker(loc)) {
        loc.marker.position = data;
        loc.marker.title = data.name;
        loc.marker.icon = loc.typeInfo.mapicon;
      } else {
        loc.marker.deattach();
        loc.marker = null;
      }
    }

    // Update routes if any.
    this.#setRoutesOnMap(loc);

    return changePromise;
  }

  removeLoc(loc) {
    loc.marker.deattach();
    loc.marker = null;
    return locations.remove(loc);
  }

  /**
   * Creates a marker for the location if needed.
   */
  #setLocOnMap(loc) {
    if (loc.notFound) {
      return loc;
    }

    // Set routes.
    this.#setRoutesOnMap(loc);

    // Set marker.
    if (!this.#shouldHaveMarker(loc) || loc.marker) {
      return loc;
    }

    assert(typeof loc.lat == 'number', `lat is not a number`);
    assert(typeof loc.lng == 'number', `lng is not a number`);

    let markerOptions = {
      map: this.#map,
      position: { lat: loc.lat, lng: loc.lng },
      icon: loc.moderationWanted
        ? loc.typeInfo.mapicon_attention
        : loc.typeInfo.mapicon,
      title: loc.name,
      visible: loc.typeInfo.zoom <= this.#map.zoom,
      clickable: true,
    };

    // Disable google place since apparently it has no benefits, but makes
    // to fail a location to change its position. Also see for markers using
    // placeid, since apparently it's not a part of standard API
    // https://stackoverflow.com/questions/29723134/google-map-add-marker-using-place-id
    // if (loc.google_id) {
    //   markerOptions.place = {
    //     placeId: loc.google_id,
    //     location: {
    //       lat: loc.lat,
    //       lng: loc.lng,
    //     },
    //   };
    // }

    let marker = new window.Marker(markerOptions);
    marker.on('click', () => this.on_marker_click(loc));
    marker.on('dblclick', () => this.on_marker_dblclick(loc));
    loc.marker = marker;

    return loc;
  }

  #setRoutesOnMap(loc) {
    let routes = loc.routes || [];
    let routeHash = routes
      .flatMap(route => route)
      .reduce((a, v) => `${a}${v.lat}${v.lng}`, '');
    if ((loc.routeHash || '') == routeHash) {
      return;
    }

    for (let gpxTrack of loc.gpxTracks || []) {
      gpxTrack.hide();
    }
    loc.gpxTracks = [];

    loc.routeHash = routeHash;
    loc.gpxTracks = routes.map(route => {
      let track = new GpxTrack({
        map: this.#map,
        name: loc.name,
        path: route,
        clickable: true,
        gradientType: 'elevation',
      });
      track.loc = loc;
      track.show();
      return track;
    });
  }

  #shouldHaveMarker(loc) {
    return loc.type != 'portage' || loc.routes.length == 0;
  }

  #activeFilter = 'all';
  #map;
}

export { MapLocations };
